import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchForexReport, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — Phase B1: foreign-currency earnings report (for NBE reporting).
const S = {
  en: {
    title: "Forex report", subtitle: "Foreign-currency earnings for NBE reporting",
    back: "Back to board", from: "From", to: "To", run: "Run",
    by_currency: "By currency", currency: "Currency", count: "Stays",
    foreign: "Foreign amount", etb: "ETB", grand: "Total (ETB)",
    guest: "Guest", date: "Date", rate: "Rate", nationality: "Nationality",
    none: "No foreign-currency stays in this range.",
  },
  am: {
    title: "የውጭ ምንዛሪ ሪፖርት", subtitle: "ለብሔራዊ ባንክ ሪፖርት የውጭ ምንዛሪ ገቢ",
    back: "ወደ ዳሽቦርድ ተመለስ", from: "ከ", to: "እስከ", run: "አሂድ",
    by_currency: "በምንዛሪ", currency: "ምንዛሪ", count: "ቆይታዎች",
    foreign: "የውጭ መጠን", etb: "ብር", grand: "ጠቅላላ (ብር)",
    guest: "እንግዳ", date: "ቀን", rate: "ተመን", nationality: "ዜግነት",
    none: "በዚህ ክልል ውስጥ የውጭ ምንዛሪ ቆይታ የለም።",
  },
  om: {
    title: "Gabaasa sharafaa", subtitle: "Galii sharafa alaa gabaasa NBE'f",
    back: "Gara gabatee deebi'i", from: "Irraa", to: "Hamma", run: "Fiigi",
    by_currency: "Sharafaan", currency: "Sharafa", count: "Turtii",
    foreign: "Hanga alaa", etb: "ETB", grand: "Ida'ama (ETB)",
    guest: "Keessummaa", date: "Guyyaa", rate: "Sarara", nationality: "Lammummaa",
    none: "Daangaa kana keessa turtiin sharafa alaa hin jiru.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const iso = (off) => { const d = new Date(); d.setDate(d.getDate() + off); return d.toISOString().slice(0, 10); };

export default function ForexReportPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const [from, setFrom] = useState(iso(-30));
  const [to, setTo] = useState(iso(0));
  const [data, setData] = useState(null);

  const load = () => { setData(null); fetchForexReport(from, to).then(setData).catch(() => setData({ items: [], summary: [], etb_total: 0 })); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Link className="muted" to="/front-desk/board" style={{ fontSize: 13, textDecoration: "none" }}>← {t("back")}</Link>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("from")} <input type="date" style={{ ...inp, marginLeft: 4 }} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("to")} <input type="date" style={{ ...inp, marginLeft: 4 }} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button className="btn primary small" onClick={load}>{t("run")}</button>
      </div>

      {data == null ? <p className="muted" style={{ marginTop: 14 }}>…</p> : (
        <>
          <div style={{ ...CARD, padding: 14, marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("by_currency")}</div>
            {(data.summary || []).length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>{t("none")}</div>
            ) : (
              <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                <thead><tr style={{ textAlign: "left", color: "#5b6472" }}>
                  <th style={{ padding: "3px 0" }}>{t("currency")}</th><th style={{ textAlign: "right" }}>{t("count")}</th>
                  <th style={{ textAlign: "right" }}>{t("foreign")}</th><th style={{ textAlign: "right" }}>{t("etb")}</th>
                </tr></thead>
                <tbody>
                  {data.summary.map((s) => (
                    <tr key={s.currency}>
                      <td style={{ padding: "3px 0", fontWeight: 600 }}>{s.currency}</td>
                      <td style={{ textAlign: "right" }}>{s.count}</td>
                      <td style={{ textAlign: "right" }}>{s.foreign_total.toLocaleString()} {s.currency}</td>
                      <td style={{ textAlign: "right" }}>{formatPrice(s.etb_total, cur)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                    <td style={{ paddingTop: 5 }}>{t("grand")}</td><td></td><td></td>
                    <td style={{ textAlign: "right", paddingTop: 5 }}>{formatPrice(data.etb_total || 0, cur)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {(data.items || []).length > 0 && (
            <div style={{ ...CARD, padding: 14, marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead><tr style={{ textAlign: "left", color: "#5b6472" }}>
                  <th style={{ padding: "3px 6px" }}>{t("date")}</th><th>{t("guest")}</th><th>{t("nationality")}</th>
                  <th style={{ textAlign: "right" }}>{t("currency")}</th><th style={{ textAlign: "right" }}>{t("rate")}</th>
                  <th style={{ textAlign: "right" }}>{t("foreign")}</th><th style={{ textAlign: "right" }}>{t("etb")}</th>
                </tr></thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={it.id}>
                      <td style={{ padding: "3px 6px" }}>{it.date}</td>
                      <td>{it.guest_name}{it.invoice_no ? ` · ${it.invoice_no}` : ""}</td>
                      <td>{it.nationality || "—"}</td>
                      <td style={{ textAlign: "right" }}>{it.currency}</td>
                      <td style={{ textAlign: "right" }}>{it.fx_rate}</td>
                      <td style={{ textAlign: "right" }}>{it.foreign_amount.toLocaleString()}</td>
                      <td style={{ textAlign: "right" }}>{formatPrice(it.etb_amount, cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
